import { getGlobalAllowedSearchParameters } from 'shared/lib/react-router-dom';

import { Environments } from './is-environment';

export enum AppDomainVerdict {
  Ok = 'ok',
  Unconfigured = 'unconfigured',
  Mismatch = 'mismatch',
}

export type AppDomainCheckInput = {
  hostname: string;
  pathname: string;
  search: string;
  environment: string;
  allowLocalhost: boolean;
};

export type AppDomainCheck =
  | { verdict: AppDomainVerdict.Ok }
  | { verdict: AppDomainVerdict.Unconfigured }
  | {
      verdict: AppDomainVerdict.Mismatch;
      redirectUrl: string;
      pathname: string;
      searchParamKeys: string[];
    };

// Encoded only so a proxy's blanket legit-host substitution cannot rewrite it into a passing match.
const domainKey = 0x5c;
const subdomainKey = 0xa7;

const encodedDomain = [114, 59, 46, 53, 44, 48, 37, 114, 61, 44, 44];

const encodedSubdomains = new Map<string, number[]>([
  [Environments.Production, [198, 215, 215]],
  [Environments.Beta, [197, 194, 211, 198]],
  [
    Environments.Development,
    [195, 194, 209, 194, 203, 200, 215, 202, 194, 201, 211],
  ],
]);

const localHostnames = ['localhost', '127.0.0.1', '::1', '[::1]'];

const ok: AppDomainCheck = { verdict: AppDomainVerdict.Ok };

const decode = (bytes: number[], key: number) =>
  String.fromCharCode(...bytes.map((byte) => byte ^ key));

export const getAppDomainCheck = ({
  hostname,
  pathname,
  search,
  environment,
  allowLocalhost,
}: AppDomainCheckInput): AppDomainCheck => {
  if (environment === Environments.Local) {
    return ok;
  }

  const encodedSubdomain = encodedSubdomains.get(environment);

  if (!encodedSubdomain) {
    return { verdict: AppDomainVerdict.Unconfigured };
  }

  const actualHost = hostname.toLowerCase();

  if (allowLocalhost && localHostnames.includes(actualHost)) {
    return ok;
  }

  const expectedHost =
    decode(encodedSubdomain, subdomainKey) + decode(encodedDomain, domainKey);

  if (actualHost === expectedHost) {
    return ok;
  }

  // A `//`-prefixed pathname resolves as a protocol-relative URL and would leave our host.
  const safePathname = `/${pathname.replace(/^\/+/, '')}`;
  const searchParams = new URLSearchParams(search);
  // The hop is unattended, so it must not launder an attacker-chosen credential onto the trusted origin.
  const allowedSearch =
    getGlobalAllowedSearchParameters(searchParams).toString();
  const query = allowedSearch ? `?${allowedSearch}` : '';

  return {
    verdict: AppDomainVerdict.Mismatch,
    redirectUrl: `https://${expectedHost}${safePathname}${query}`,
    pathname: safePathname,
    searchParamKeys: [...new Set(searchParams.keys())],
  };
};
